/**
 * Product-family colour and order, shared by every breakdown, legend and compare view.
 *
 * Order is fixed (alphabetical, case-insensitive) so scenarios can be read one below another.
 * Colour follows the family's quarter-to-date share rank within its therapeutic area, so the
 * largest family always gets the primary blue; the same inputs give the same colour on every page.
 */
export const FAMILY_PALETTE = ['#0E4C92', '#5B8FD0', '#2A8C82', '#C28A2C', '#7A5AA6', '#8A97A8', '#C2685E', '#5FA8D3', '#6E8B3D', '#B5739E'];
export const FAMILY_FALLBACK_COLOR = '#A3AEBB';

const nameKey = (name: string) => name.trim().toLocaleLowerCase();

export const compareFamilyNames = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

/** Returns a copy of `items` in the fixed family order. */
export const sortByFamily = <T,>(items: readonly T[], getName: (item: T) => string): T[] =>
  [...items].sort((a, b) => compareFamilyNames(getName(a), getName(b)));

export interface FamilyShare { name: string; share?: number | null }

/** Colour per family name, by descending share (ties and unknown shares fall back to name order). */
export const buildFamilyColors = (families: readonly FamilyShare[]): Map<string, string> => {
  const ranked = [...families].sort((a, b) => (Number(b.share ?? -1) - Number(a.share ?? -1)) || compareFamilyNames(a.name, b.name));
  const colors = new Map<string, string>();
  ranked.forEach((family, index) => {
    const key = nameKey(family.name);
    if (!colors.has(key)) colors.set(key, FAMILY_PALETTE[index % FAMILY_PALETTE.length]);
  });
  return colors;
};

export const familyColor = (colors: Map<string, string>, name: string) => colors.get(nameKey(name)) ?? FAMILY_FALLBACK_COLOR;
